// Copyright (c) 2026 Nathan Blackman / Find My Trout. All rights reserved.

import React, { useState, useEffect } from 'react';
import { X, Mail, Lock, Eye, EyeOff, User, FileText } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const SettingsModal = ({ isOpen, onClose }) => {
  const { currentUser, userProfile, changeEmail, changePassword, updateUserProfile } = useAuth();
  const [displayName, setDisplayName] = useState('');
  const [bio, setBio] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [currentEmail, setCurrentEmail] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showEmailPassword, setShowEmailPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    if (isOpen && currentUser) {
      setDisplayName(userProfile?.displayName || currentUser?.displayName || '');
      setBio(userProfile?.bio || '');
      setCurrentEmail(currentUser.email || '');
      setNewEmail('');
      setEmailPassword('');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setProfileError('');
      setEmailError('');
      setPasswordError('');
    }
  }, [isOpen, currentUser, userProfile]);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setProfileError('');
    const trimmedName = displayName.trim();
    if (!trimmedName) {
      setProfileError('Name is required.');
      return;
    }
    setProfileLoading(true);
    try {
      await updateUserProfile(trimmedName, bio.trim());
      onClose();
    } catch (err) {
      setProfileError(err.message || 'Failed to update profile. Try again.');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    setEmailError('');
    if (!newEmail.trim()) {
      setEmailError('Enter a new email address.');
      return;
    }
    if (newEmail.trim() === currentEmail) {
      setEmailError('New email must be different from current email.');
      return;
    }
    if (!emailPassword) {
      setEmailError('Enter your current password to confirm.');
      return;
    }
    setEmailLoading(true);
    try {
      await changeEmail(newEmail.trim(), emailPassword);
      setNewEmail('');
      setEmailPassword('');
      onClose();
    } catch (err) {
      const msg = err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password'
        ? 'Current password is incorrect.'
        : err.code === 'auth/email-already-in-use'
          ? 'That email is already in use.'
          : err.code === 'auth/invalid-email'
            ? 'Please enter a valid email address.'
            : err.message || 'Failed to update email. Try again.';
      setEmailError(msg);
    } finally {
      setEmailLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Fill in all password fields.');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match.');
      return;
    }
    setPasswordLoading(true);
    try {
      await changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      onClose();
    } catch (err) {
      const msg = err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password'
        ? 'Current password is incorrect.'
        : err.code === 'auth/weak-password'
          ? 'New password is too weak.'
          : err.message || 'Failed to update password. Try again.';
      setPasswordError(msg);
    } finally {
      setPasswordLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content settings-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2 className="modal-title">Edit profile</h2>
            <p className="modal-subtitle">Update your name, bio, email, and password</p>
          </div>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Close">
            <X size={24} />
          </button>
        </div>

        <div className="settings-modal-body">
          {/* Name & Bio */}
          <form onSubmit={handleUpdateProfile} className="settings-form">
            <div className="form-section-header">
              <User size={20} />
              <span>Profile</span>
            </div>
            <div className="form-section">
              <label className="settings-label">Name</label>
              <input
                type="text"
                className="settings-input"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Your display name"
                autoComplete="name"
                disabled={profileLoading}
              />
              <label className="settings-label">Bio</label>
              <textarea
                className="settings-input settings-textarea"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="Tell others about yourself..."
                rows={3}
                disabled={profileLoading}
              />
              {profileError && <p className="settings-error">{profileError}</p>}
              <button type="submit" className="settings-submit-btn" disabled={profileLoading}>
                {profileLoading ? 'Saving…' : 'Save profile'}
              </button>
            </div>
          </form>

          {/* Current email (private) */}
          <div className="settings-section">
            <div className="form-section-header">
              <Mail size={20} />
              <span>Current email</span>
            </div>
            <p className="settings-email-display">{currentEmail}</p>
            <p className="settings-hint">Your email is only visible here and is not shown on your public profile.</p>
          </div>

          {/* Change email */}
          <form onSubmit={handleChangeEmail} className="settings-form">
            <div className="form-section-header">
              <Mail size={20} />
              <span>Change email</span>
            </div>
            <div className="form-section">
              <label className="settings-label">New email address</label>
              <input
                type="email"
                className="settings-input"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="new@example.com"
                autoComplete="email"
                disabled={emailLoading}
              />
              <label className="settings-label">Current password (to confirm)</label>
              <div className="settings-password-wrap">
                <input
                  type={showEmailPassword ? 'text' : 'password'}
                  className="settings-input"
                  value={emailPassword}
                  onChange={(e) => setEmailPassword(e.target.value)}
                  placeholder="Enter current password"
                  autoComplete="current-password"
                  disabled={emailLoading}
                />
                <button
                  type="button"
                  className="settings-password-toggle"
                  onClick={() => setShowEmailPassword((s) => !s)}
                  aria-label={showEmailPassword ? 'Hide password' : 'Show password'}
                >
                  {showEmailPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {emailError && <p className="settings-error">{emailError}</p>}
              <button type="submit" className="settings-submit-btn" disabled={emailLoading}>
                {emailLoading ? 'Updating…' : 'Update email'}
              </button>
            </div>
          </form>

          {/* Change password */}
          <form onSubmit={handleChangePassword} className="settings-form">
            <div className="form-section-header">
              <Lock size={20} />
              <span>Change password</span>
            </div>
            <div className="form-section">
              <label className="settings-label">Current password</label>
              <div className="settings-password-wrap">
                <input
                  type={showCurrentPassword ? 'text' : 'password'}
                  className="settings-input"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  autoComplete="current-password"
                  disabled={passwordLoading}
                />
                <button
                  type="button"
                  className="settings-password-toggle"
                  onClick={() => setShowCurrentPassword((s) => !s)}
                  aria-label={showCurrentPassword ? 'Hide password' : 'Show password'}
                >
                  {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <label className="settings-label">New password</label>
              <div className="settings-password-wrap">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  className="settings-input"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  autoComplete="new-password"
                  disabled={passwordLoading}
                />
                <button
                  type="button"
                  className="settings-password-toggle"
                  onClick={() => setShowNewPassword((s) => !s)}
                  aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                >
                  {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <label className="settings-label">Confirm new password</label>
              <input
                type="password"
                className="settings-input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                autoComplete="new-password"
                disabled={passwordLoading}
              />
              {passwordError && <p className="settings-error">{passwordError}</p>}
              <button type="submit" className="settings-submit-btn" disabled={passwordLoading}>
                {passwordLoading ? 'Updating…' : 'Update password'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
